import React, { useEffect, useMemo, useState, useCallback } from "react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useBrands } from "../contexts/BrandContext";
import { toast } from "sonner";
import { formatDateFr } from "../lib/dateFormat";
import { Package, Check, X, Search, Filter, RefreshCw, Inbox, ClipboardList, Trash2 } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "./ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "./ui/select";
import SignaturePad from "./SignaturePad";
import EquipmentSiteRequestsTab from "./EquipmentSiteRequestsTab";
import { Building2, User2 } from "lucide-react";
import MenuBadge from "./MenuBadge";
import { useNotificationCounts } from "../contexts/NotificationCountsContext";

const STATUS_BADGE = {
  en_attente: { label: "En attente", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  approuve: { label: "Approuvée", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  refuse: { label: "Refusée", cls: "bg-red-50 text-red-700 border-red-200" },
};

const REASON_LABEL = { usure: "Usure", defaut: "Défaut", perte: "Perte", autre: "Autre" };

const REQUEST_TYPE_BADGE = {
  remplacement:   { label: "Remplacement",   cls: "bg-sky-50 text-sky-800 border-sky-200" },
  nouveau_besoin: { label: "Nouveau besoin", cls: "bg-purple-50 text-purple-800 border-purple-200" },
};

/**
 * Section "Demandes de matériels" — visible uniquement par admin / manager.
 * Liste toutes les demandes de remplacement matériel envoyées par les collaborateurs
 * et permet à l'admin/manager de valider ou refuser une demande directement depuis cette vue.
 */
export default function EquipmentRequestsTab() {
  const { user } = useAuth();
  const { selectedBrandId, brands } = useBrands();
  const [scope, setScope] = useState("collab"); // "collab" | "site"
  const { byPageTab } = useNotificationCounts();
  const collabBadge = byPageTab["equipements.requests.collab"] || 0;
  const siteBadge = byPageTab["equipements.requests.site"] || 0;
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("en_attente");
  const [search, setSearch] = useState("");

  // Décision modale
  const [decisionReq, setDecisionReq] = useState(null);
  const [decisionMode, setDecisionMode] = useState("approuve");
  const [decisionForm, setDecisionForm] = useState({
    controller_signature: null,
    quantite_validee: 1,
    decision_note: "",
  });

  const canDecide = user?.role === "admin" || user?.role === "manager";
  const isAdmin = user?.role === "admin";
  const [deletingId, setDeletingId] = useState("");

  const deleteRequest = async (r) => {
    const lbl = r.designation_snapshot || "—";
    const who = r.requester_name || "—";
    const date = r.created_at ? new Date(r.created_at).toLocaleDateString("fr-FR") : "—";
    if (!window.confirm(
      `Supprimer définitivement cette demande de matériel ?\n\n`
      + `• Collaborateur : ${who}\n`
      + `• Matériel : ${lbl}\n`
      + `• Créée le : ${date}\n\n`
      + `Cette action est irréversible. La ligne disparaîtra de l'historique sans modifier le stock ni les affectations en cours.`
    )) return;
    try {
      setDeletingId(r.id);
      await api.delete(`/equipment-requests/${r.id}`);
      toast.success("Demande supprimée");
      await load();
      window.dispatchEvent(new CustomEvent("plania:equipment-requests-updated"));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de la suppression");
    } finally {
      setDeletingId("");
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // On charge TOUTES les demandes (tous statuts) puis on filtre côté client
      // afin d'afficher des compteurs par onglet sans multiplier les requêtes.
      const params = {};
      if (selectedBrandId) params.brand_id = selectedBrandId;
      const res = await api.get("/equipment-requests", { params });
      setRequests(res.data || []);
    } catch (e) {
      toast.error("Erreur lors du chargement des demandes");
    } finally {
      setLoading(false);
    }
  }, [selectedBrandId]);

  useEffect(() => { load(); }, [load]);

  // Rafraîchissement temps réel à la réception d'une notification matériel
  // OU lors d'une décision prise depuis le tableau de bord.
  useEffect(() => {
    const onNotif = (ev) => {
      const n = ev?.detail;
      if (!n) return;
      if (n.type === "equipment_request" || n.type === "equipment_request_decision") {
        load();
      }
    };
    const onUpdated = () => load();
    window.addEventListener("plania:notification", onNotif);
    window.addEventListener("plania:equipment-requests-updated", onUpdated);
    const pollId = setInterval(load, 60_000);
    return () => {
      window.removeEventListener("plania:notification", onNotif);
      window.removeEventListener("plania:equipment-requests-updated", onUpdated);
      clearInterval(pollId);
    };
  }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let base = requests;
    if (statusFilter !== "all") {
      base = base.filter((r) => r.status === statusFilter);
    }
    if (!q) return base;
    return base.filter((r) => (
      (r.requester_name || "").toLowerCase().includes(q)
      || (r.designation_snapshot || "").toLowerCase().includes(q)
      || (r.ref_id_snapshot || "").toLowerCase().includes(q)
      || (r.besoin_titre || "").toLowerCase().includes(q)
      || (r.besoin_description || "").toLowerCase().includes(q)
      || (r.reason || "").toLowerCase().includes(q)
    ));
  }, [requests, search, statusFilter]);

  const counts = useMemo(() => ({
    en_attente: requests.filter((r) => r.status === "en_attente").length,
    approuve: requests.filter((r) => r.status === "approuve").length,
    refuse: requests.filter((r) => r.status === "refuse").length,
    all: requests.length,
  }), [requests]);

  const openDecision = (req, mode) => {
    setDecisionReq(req);
    setDecisionMode(mode);
    setDecisionForm({
      controller_signature: null,
      quantite_validee: req.quantite_demandee || 1,
      decision_note: "",
    });
  };

  const submitDecision = async () => {
    if (!decisionReq) return;
    if (decisionMode === "approuve") {
      if (!decisionForm.controller_signature) {
        toast.error("Veuillez signer pour valider la demande");
        return;
      }
      const qty = Number(decisionForm.quantite_validee);
      if (!qty || qty < 1) {
        toast.error("Quantité validée invalide");
        return;
      }
    }
    try {
      await api.put(`/equipment-requests/${decisionReq.id}/decision`, {
        status: decisionMode,
        controller_signature: decisionMode === "approuve" ? decisionForm.controller_signature : null,
        quantite_validee: decisionMode === "approuve" ? Number(decisionForm.quantite_validee) : null,
        decision_note: decisionForm.decision_note || "",
      });
      toast.success(
        decisionMode === "approuve"
          ? (decisionReq.request_type === "nouveau_besoin"
              ? "Nouveau besoin approuvé"
              : "Demande approuvée — nouvelle affectation créée")
          : "Demande refusée"
      );
      setDecisionReq(null);
      load();
      // Synchronise le tableau de bord et autres listes ouvertes
      window.dispatchEvent(new CustomEvent("plania:equipment-requests-updated"));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const pendingCount = counts.en_attente;

  return (
    <div className="space-y-4" data-testid="equipment-requests-tab">
      <div className="bg-white border border-zinc-200 rounded-sm p-2 inline-flex gap-1" role="tablist" aria-label="Type de demande">
        <button
          type="button" role="tab" aria-selected={scope === "collab"}
          onClick={() => setScope("collab")}
          data-testid="equip-req-scope-collab"
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold uppercase tracking-wider border rounded-sm transition ${scope === "collab" ? "bg-zinc-900 text-white border-zinc-900" : "border-zinc-200 text-zinc-700 hover:bg-zinc-50"}`}
        >
          <User2 size={12} /> Demandes collaborateurs
          <MenuBadge
            count={collabBadge}
            size="xs"
            className="ml-1"
            testid="equip-req-scope-collab-badge"
          />
        </button>
        <button
          type="button" role="tab" aria-selected={scope === "site"}
          onClick={() => setScope("site")}
          data-testid="equip-req-scope-site"
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold uppercase tracking-wider border rounded-sm transition ${scope === "site" ? "bg-zinc-900 text-white border-zinc-900" : "border-zinc-200 text-zinc-700 hover:bg-zinc-50"}`}
        >
          <Building2 size={12} /> Demandes sites
          <MenuBadge
            count={siteBadge}
            size="xs"
            className="ml-1"
            testid="equip-req-scope-site-badge"
          />
        </button>
      </div>

      {scope === "site" && <EquipmentSiteRequestsTab />}

      {scope === "collab" && (
      <>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="label-uppercase mb-1 inline-flex items-center gap-1.5">
            <ClipboardList size={11} /> Validation
          </p>
          <h2 className="font-display font-semibold text-xl text-zinc-900">
            Demandes de matériels
          </h2>
          <p className="text-xs text-zinc-600 mt-1 font-mono">
            Demandes envoyées par les collaborateurs (remplacements & nouveaux besoins) · {pendingCount} en attente
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={load}
          disabled={loading}
          data-testid="equip-req-refresh-btn"
        >
          <RefreshCw size={13} className={`mr-1.5 ${loading ? "animate-spin" : ""}`} />
          Rafraîchir
        </Button>
      </div>

      {/* Onglets de statut + recherche */}
      <div className="bg-white border border-zinc-200 rounded-sm p-3 space-y-3">
        <div className="flex flex-wrap items-center gap-1.5" role="tablist" aria-label="Filtre par statut">
          {[
            { key: "en_attente", label: "En attente", cls: "border-amber-200 text-amber-800 bg-amber-50", activeCls: "bg-amber-600 text-white border-amber-700" },
            { key: "approuve", label: "Approuvées", cls: "border-emerald-200 text-emerald-800 bg-emerald-50", activeCls: "bg-emerald-600 text-white border-emerald-700" },
            { key: "refuse", label: "Refusées", cls: "border-red-200 text-red-800 bg-red-50", activeCls: "bg-red-600 text-white border-red-700" },
            { key: "all", label: "Toutes", cls: "border-zinc-200 text-zinc-700 bg-zinc-50", activeCls: "bg-zinc-900 text-white border-zinc-900" },
          ].map((t) => {
            const active = statusFilter === t.key;
            return (
              <button
                key={t.key}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setStatusFilter(t.key)}
                data-testid={`equip-req-tab-${t.key}`}
                className={`inline-flex items-center gap-2 px-3 py-1.5 text-xs font-semibold uppercase tracking-wider border rounded-sm transition ${active ? t.activeCls : `${t.cls} hover:brightness-95`}`}
              >
                {t.label}
                <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded-sm border ${active ? "bg-white/20 border-white/30 text-white" : "bg-white border-zinc-200 text-zinc-700"}`}>
                  {counts[t.key]}
                </span>
              </button>
            );
          })}
        </div>
        <div>
          <Label className="label-uppercase mb-1.5 block inline-flex items-center gap-1.5">
            <Search size={10} /> Recherche
          </Label>
          <Input
            placeholder="Collaborateur, désignation, référence ou motif…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="equip-req-search-input"
          />
        </div>
      </div>

      {/* Liste */}
      <div className="bg-white border border-zinc-200 rounded-sm">
        {loading && filtered.length === 0 ? (
          <p className="text-sm text-zinc-500 font-mono py-10 text-center">Chargement…</p>
        ) : filtered.length === 0 ? (
          <div className="py-12 text-center" data-testid="equip-req-empty">
            <Inbox className="mx-auto text-zinc-300 mb-3" size={36} />
            <p className="text-sm text-zinc-500 font-mono">
              Aucune demande {statusFilter === "en_attente" ? "en attente" : ""} pour le moment.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-zinc-100">
            {filtered.map((r) => {
              const badge = STATUS_BADGE[r.status] || STATUS_BADGE.en_attente;
              const brand = brands.find((b) => b.id === r.brand_id);
              const typeBadge = REQUEST_TYPE_BADGE[r.request_type] || REQUEST_TYPE_BADGE.remplacement;
              const isNouveauBesoin = r.request_type === "nouveau_besoin";
              // Un manager ne peut PAS décider sur une demande émise par un autre manager.
              const canDecideOnThis =
                canDecide &&
                r.status === "en_attente" &&
                !(user?.role === "manager" && r.requester_role === "manager");
              return (
                <div
                  key={r.id}
                  className="p-4 flex items-start gap-3 flex-wrap"
                  data-testid={`equip-req-tab-row-${r.id}`}
                >
                  <span
                    className="h-12 w-1 rounded-sm flex-shrink-0"
                    style={{ backgroundColor: brand?.color || "#0EA5A4" }}
                  />
                  <div className="flex-1 min-w-[240px]">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className={`inline-flex items-center gap-1 text-[10px] uppercase font-semibold px-2 py-0.5 border rounded-sm tracking-wider ${typeBadge.cls}`}>
                        <Package size={10} /> {typeBadge.label}
                      </span>
                      <p className="text-sm font-medium text-zinc-900">
                        {r.requester_name || "Collaborateur"}
                      </p>
                      {r.requester_role && (
                        <span className="text-[10px] uppercase font-semibold px-2 py-0.5 border rounded-sm tracking-wider bg-zinc-50 border-zinc-200 text-zinc-600">
                          {r.requester_role}
                        </span>
                      )}
                      {!isNouveauBesoin && r.reason_type && (
                        <span className="text-[10px] uppercase font-semibold px-2 py-0.5 border rounded-sm tracking-wider bg-zinc-50 border-zinc-200 text-zinc-700">
                          {REASON_LABEL[r.reason_type] || r.reason_type}
                        </span>
                      )}
                      <span className={`text-[10px] uppercase font-semibold px-2 py-0.5 border rounded-sm tracking-wider ${badge.cls}`}>
                        {badge.label}
                      </span>
                      {r.auto_validated && (
                        <span className="text-[10px] uppercase font-semibold px-2 py-0.5 border rounded-sm tracking-wider bg-emerald-50 border-emerald-200 text-emerald-700">
                          Auto-validée
                        </span>
                      )}
                    </div>
                    {isNouveauBesoin ? (
                      <>
                        <p className="text-sm text-zinc-800 mt-1">
                          <b>{r.besoin_titre || "—"}</b>
                        </p>
                        {r.besoin_description && (
                          <p className="text-xs text-zinc-600 mt-1">{r.besoin_description}</p>
                        )}
                        {r.suggestion_url && (
                          <p className="text-xs text-blue-600 font-mono mt-0.5 truncate">
                            <a href={r.suggestion_url} target="_blank" rel="noreferrer" className="hover:underline">
                              {r.suggestion_url}
                            </a>
                          </p>
                        )}
                      </>
                    ) : (
                      <p className="text-sm text-zinc-800 mt-1">
                        <b>{r.designation_snapshot || "—"}</b>
                        {r.ref_id_snapshot ? (
                          <span className="text-xs font-mono text-zinc-500 ml-2">({r.ref_id_snapshot})</span>
                        ) : null}
                      </p>
                    )}
                    <p className="text-xs text-zinc-600 font-mono mt-0.5">
                      Qté demandée : <b>{r.quantite_demandee || 1}</b>
                      {r.quantite_validee ? <> · Qté validée : <b>{r.quantite_validee}</b></> : null}
                      {brand?.name ? <> · Département : {brand.name}</> : null}
                    </p>
                    {r.reason && !isNouveauBesoin && (
                      <p className="text-xs text-zinc-600 mt-1 italic">« {r.reason} »</p>
                    )}
                    {r.decision_note && (
                      <p className="text-xs text-zinc-600 mt-1">
                        <span className="font-mono text-zinc-500">Note décision :</span> {r.decision_note}
                      </p>
                    )}
                    <p className="text-[11px] text-zinc-400 font-mono mt-1">
                      Créée le {r.created_at ? formatDateFr(r.created_at.slice(0, 10)) : "—"}
                      {r.decided_at ? ` · Décidée le ${formatDateFr(r.decided_at.slice(0, 10))}` : ""}
                    </p>
                  </div>
                  {(canDecide || isAdmin) && (
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {canDecideOnThis && (
                        <>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => openDecision(r, "approuve")}
                            className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                            data-testid={`equip-req-tab-approve-${r.id}`}
                          >
                            <Check size={13} className="mr-1.5" /> Approuver
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => openDecision(r, "refuse")}
                            className="border-red-200 text-red-700 hover:bg-red-50"
                            data-testid={`equip-req-tab-refuse-${r.id}`}
                          >
                            <X size={13} className="mr-1.5" /> Refuser
                          </Button>
                        </>
                      )}
                      {canDecide && r.status === "en_attente" && !canDecideOnThis && user?.role === "manager" && r.requester_role === "manager" && (
                        <span
                          className="text-[11px] font-mono text-zinc-500 italic px-2"
                          data-testid={`equip-req-tab-locked-${r.id}`}
                          title="Seul un administrateur peut valider cette demande"
                        >
                          Validation admin requise
                        </span>
                      )}
                      {isAdmin && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => deleteRequest(r)}
                          disabled={deletingId === r.id}
                          className="h-8 px-2 text-red-700 border-red-200 hover:bg-red-50"
                          data-testid={`equip-req-tab-delete-${r.id}`}
                          title="Supprimer cette ligne (admin)"
                        >
                          <Trash2 size={13} />
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Modale de décision (signature contrôleur obligatoire pour validation) */}
      <Dialog open={!!decisionReq} onOpenChange={(v) => { if (!v) setDecisionReq(null); }}>
        <DialogContent
          className="flex flex-col max-h-[90vh] p-0 sm:max-w-lg"
          data-testid="equip-req-tab-decision-dialog"
        >
          <DialogHeader className="px-6 pt-6 pb-4 border-b border-zinc-100">
            <DialogTitle>
              {decisionMode === "approuve" ? "Valider la demande de matériel" : "Refuser la demande de matériel"}
            </DialogTitle>
            <DialogDescription className="sr-only">
              {decisionMode === "approuve"
                ? "Validation d'une demande de remplacement — signature contrôleur obligatoire."
                : "Refus d'une demande de remplacement — précisez le motif si nécessaire."}
            </DialogDescription>
          </DialogHeader>
          {decisionReq && (
            <div className="overflow-y-auto px-6 py-4 flex-1 space-y-4">
              <div className="bg-zinc-50 border border-zinc-200 rounded-sm p-3 text-sm space-y-1">
                <p>
                  <b>{decisionReq.designation_snapshot || "—"}</b>
                  <span className="text-xs font-mono text-zinc-600 ml-2">({decisionReq.ref_id_snapshot || "—"})</span>
                </p>
                <p className="text-xs font-mono text-zinc-600">
                  Demandeur : {decisionReq.requester_name || "—"} · Qté demandée : <b>{decisionReq.quantite_demandee || 1}</b>
                </p>
                <p className="text-xs font-mono text-zinc-600">
                  Motif : {decisionReq.reason_type}{decisionReq.reason ? ` — ${decisionReq.reason}` : ""}
                </p>
              </div>

              {decisionMode === "approuve" && (
                <>
                  <div>
                    <Label className="label-uppercase mb-1.5 block">
                      Quantité validée <span className="text-red-600">*</span>
                    </Label>
                    <Input
                      type="number" min="1"
                      value={decisionForm.quantite_validee}
                      onChange={(e) => setDecisionForm({ ...decisionForm, quantite_validee: e.target.value })}
                      data-testid="equip-req-tab-qty-input"
                    />
                  </div>
                  <div>
                    <Label className="label-uppercase mb-1.5 block">
                      Signature contrôleur ({user?.name}) <span className="text-red-600">*</span>
                    </Label>
                    <SignaturePad
                      onSave={(url) => setDecisionForm((f) => ({ ...f, controller_signature: url }))}
                      value={decisionForm.controller_signature}
                    />
                    <p className="text-[11px] text-zinc-500 font-mono mt-1">
                      Le collaborateur devra ensuite signer la réception du nouveau matériel depuis Mon profil.
                    </p>
                  </div>
                </>
              )}

              <div>
                <Label className="label-uppercase mb-1.5 block">
                  Note de décision {decisionMode === "refuse" ? <span className="text-zinc-400">(recommandé)</span> : <span className="text-zinc-400">(optionnel)</span>}
                </Label>
                <Textarea
                  rows={3}
                  value={decisionForm.decision_note}
                  onChange={(e) => setDecisionForm({ ...decisionForm, decision_note: e.target.value })}
                  placeholder={decisionMode === "refuse" ? "Précisez la raison du refus…" : "Commentaire interne…"}
                  data-testid="equip-req-tab-note-input"
                />
              </div>
            </div>
          )}
          <DialogFooter className="px-6 py-4 border-t border-zinc-100 bg-white">
            <Button variant="outline" onClick={() => setDecisionReq(null)}>Annuler</Button>
            <Button
              onClick={submitDecision}
              disabled={decisionMode === "approuve" && !decisionForm.controller_signature}
              className={decisionMode === "approuve" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-red-600 hover:bg-red-700"}
              data-testid="equip-req-tab-decision-submit"
            >
              {decisionMode === "approuve" ? "Valider la demande" : "Confirmer le refus"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </>
      )}
    </div>
  );
}
